import React, { useState, useCallback, useEffect, useMemo } from "react";
import { toFroApi } from "../api/toFroRegisterApi";
import GlobalFeedback from "../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import { useToAndFroRole } from "../utils/useToAndFroRole";
import { TOS_FROS_SUBMODULE_SLUG } from "../utils/toAndFroPermissionSlugs";
import MarkReturnedModal from "../components/register/MarkReturnedModal";
import ViewMovementModal from "../components/register/ViewMovementModal";
import OfficeToAndFroDashboardMetricsSection from "../components/register/sections/OfficeToAndFroDashboardMetricsSection";
import OfficeToAndFroFormSection from "../components/register/sections/OfficeToAndFroFormSection";
import OfficeToAndFroDirectorySection, {
  TO_AND_FRO_DIRECTORY_COLUMN_DEFS,
  createOfficeToFroInitialColumnVisibility,
  toFroDefaultDirectoryFilters,
  toFroDirectoryCellValue,
} from "../components/register/sections/OfficeToAndFroDirectorySection";
import OfficeToAndFroQuickJumpSection from "../components/register/sections/OfficeToAndFroQuickJumpSection";

import "../../user-builder/styles/userBuilder.css";
import "../../user-builder/styles/UserBuilderPage.css";

function safeErrorText(err, fallback = "Internal server error. Please try again later.") {
  const m = err?.message;
  return typeof m === "string" && m.trim() ? m : fallback;
}

const DIRECTORY_PAGE_SIZE = 10;

const QUICK_JUMP_BASE = [
  { key: "stats", label: "Performance overview", targetId: "otf-stats" },
  { key: "add", label: "Movement entry", targetId: "otf-add" },
  { key: "list", label: "Directory", targetId: "otf-list" },
];

function filterQuickJump(items, { showSummary, showCreate, showDirectory }) {
  return items.filter((item) => {
    if (item.key === "stats") return showSummary;
    if (item.key === "add") return showCreate;
    if (item.key === "list") return showDirectory;
    return true;
  });
}

const ToAndFroPage = () => {
  const perms = useToAndFroRole(TOS_FROS_SUBMODULE_SLUG);
  const showSummary = perms.canView;
  const showCreate = !perms.permissionsLoading && perms.canCreate;
  const showDirectory = perms.canView;
  const canReturnActions = !perms.permissionsLoading && perms.canReturn;
  const hasAnyAccess = showSummary || showCreate || showDirectory;
  const [stats, setStats] = useState(null);
  const [movements, setMovements] = useState([]);
  const [masters, setMasters] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingMovements, setLoadingMovements] = useState(true);
  const [directoryPage, setDirectoryPage] = useState(1);
  const [directoryTotal, setDirectoryTotal] = useState(0);
  const [directoryTotalPages, setDirectoryTotalPages] = useState(1);
  const [draftDirectoryFilters, setDraftDirectoryFilters] = useState(() =>
    toFroDefaultDirectoryFilters(),
  );
  const [appliedDirectoryFilters, setAppliedDirectoryFilters] = useState(() =>
    toFroDefaultDirectoryFilters(),
  );
  const [columnVisibility, setColumnVisibility] = useState(createOfficeToFroInitialColumnVisibility);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [activeQuickJump, setActiveQuickJump] = useState(null);

  const [selectedViewMovementId, setSelectedViewMovementId] = useState(null);
  const [selectedMarkReturnedMovement, setSelectedMarkReturnedMovement] = useState(null);

  const masterDataForForm = useMemo(
    () => ({
      staff: masters?.users || [],
      purposes: masters?.purposes || [],
      statuses: masters?.statuses || [],
    }),
    [masters],
  );

  const getToAndFroExportData = useCallback(() => {
    const visibleCols = TO_AND_FRO_DIRECTORY_COLUMN_DEFS.filter((c) => columnVisibility[c.id]);
    const headers = visibleCols.map((c) => c.label);
    const rows = movements.map((m) =>
      visibleCols.map((c) => {
        const v = toFroDirectoryCellValue(c.id, m, masters);
        return typeof v === "string" || typeof v === "number" ? v : String(v ?? "");
      }),
    );
    return { headers, rows };
  }, [movements, columnVisibility, masters]);

  const directoryQueryParams = useMemo(() => {
    const q = {
      page: directoryPage,
      limit: DIRECTORY_PAGE_SIZE,
    };
    const user = String(appliedDirectoryFilters.user_search || "").trim();
    if (user) q.name = user;

    const col = appliedDirectoryFilters.column;
    const val = String(appliedDirectoryFilters.value || "").trim();
    if (col && val) {
      if (col === "movement_code") q.movement_code = val;
      if (col === "movement_date") q.movement_date = val;
      if (col === "staff_name") q.staff_name = val;
      if (col === "designation") q.designation = val;
      if (col === "purpose_name") {
        const p = (masters?.purposes || []).find((x) => String(x.purpose_name) === val);
        if (p?.id != null) q.purpose_id = String(p.id);
      }
      if (col === "place_visited") q.place_visited = val;
      if (col === "status_name") {
        const s = (masters?.statuses || []).find((x) => String(x.status_name) === val);
        if (s?.id != null) q.status_id = String(s.id);
      }
      if (col === "out_time") q.out_time_contains = val;
      if (col === "in_time") q.in_time_contains = val;
      if (col === "recorded_by" && val) q.created_by_user_id = String(val).trim();
    }
    return q;
  }, [directoryPage, appliedDirectoryFilters, masters]);

  const loadDirectoryFilterOptions = useCallback(async (column) => {
    const res = await toFroApi.getMovementFilterOptions(column);
    if (!res?.success) return [];
    return res.data?.options ?? [];
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await toFroApi.getMovementStats();
      if (response?.success) {
        setStats(response.data ?? response);
      } else {
        setStats(response?.data ?? response);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const loadMasters = useCallback(async () => {
    try {
      const response = await toFroApi.getMasterData();
      if (response?.success) {
        setMasters(response.data ?? response);
      } else {
        setMasters(response?.data ?? response);
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchMovementsList = useCallback(async () => {
    setLoadingMovements(true);
    setErrorMsg("");
    try {
      const data = await toFroApi.getMovements(directoryQueryParams);
      if (!data?.success) {
        setErrorMsg(data?.message || "Failed to load movements");
        setMovements([]);
        return;
      }
      setMovements(data.movements || []);
      const meta = data.meta || {};
      setDirectoryTotal(Number(meta.total || 0));
      setDirectoryTotalPages(Math.max(1, Number(meta.totalPages || 1)));
      setDirectoryPage(Number(meta.page || 1));
    } catch (err) {
      setErrorMsg(safeErrorText(err));
      setMovements([]);
    } finally {
      setLoadingMovements(false);
    }
  }, [directoryQueryParams]);

  useEffect(() => {
    if (perms.permissionsLoading || !showSummary) return;
    fetchStats();
  }, [fetchStats, perms.permissionsLoading, showSummary]);

  useEffect(() => {
    if (perms.permissionsLoading || !showSummary) return;
    loadMasters();
  }, [loadMasters, perms.permissionsLoading, showSummary]);

  useEffect(() => {
    const t = setTimeout(() => setAppliedDirectoryFilters({ ...draftDirectoryFilters }), 350);
    return () => clearTimeout(t);
  }, [draftDirectoryFilters]);

  useEffect(() => {
    setDirectoryPage(1);
  }, [appliedDirectoryFilters]);

  useEffect(() => {
    if (perms.permissionsLoading || !showDirectory) return;
    fetchMovementsList();
  }, [fetchMovementsList, perms.permissionsLoading, showDirectory]);

  const bumpAfterMutation = useCallback(() => {
    fetchStats();
    fetchMovementsList();
  }, [fetchStats, fetchMovementsList]);

  const handleViewMovement = useCallback((movement) => {
    setSelectedViewMovementId(movement.id);
  }, []);

  const handleReturnMovement = useCallback((movement) => {
    setSelectedMarkReturnedMovement(movement);
  }, []);

  const onMarkReturnedConfirm = useCallback(() => {
    setSuccessMsg("Movement marked as returned successfully");
    setSelectedMarkReturnedMovement(null);
    bumpAfterMutation();
  }, [bumpAfterMutation]);

  const handleMovementCreated = useCallback((msg) => {
    setSuccessMsg(msg || "Movement entry saved successfully");
    bumpAfterMutation();
  }, [bumpAfterMutation]);

  const quickJumpItems = useMemo(
    () =>
      filterQuickJump(QUICK_JUMP_BASE, {
        showSummary,
        showCreate,
        showDirectory,
      }),
    [showSummary, showCreate, showDirectory],
  );

  return (
    <div className="user-builder-shell">
      <header className="user-builder-hero">
        <div className="user-builder-hero__text">
          <p className="user-builder-hero__eyebrow">Office administration</p>
          <h2 className="user-builder-hero__title">To and Fro management</h2>
          <p className="user-builder-hero__sub">
            Track staff and volunteer movements, log new entries, and review the directory.
          </p>
        </div>
      </header>

      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <GlobalFeedback type="error" text={errorMsg} />
      <GlobalFeedback type="error" text={perms.permissionsError} role="alert" />
      {perms.permissionsLoading ? (
        <p className="schedule-table-loading" style={{ padding: "2rem 1.5rem" }}>
          Loading permissions…
        </p>
      ) : null}
      {!perms.permissionsLoading && !hasAnyAccess ? (
        <GlobalFeedback
          type="warning"
          text="No permission for to-and-fro register. Enable View and/or Create on tos-fros in User builder for this role, then sign in again."
          role="status"
        />
      ) : null}

      <div className="user-builder-layout">
        <div className="user-builder-layout__main">
          {!perms.permissionsLoading && showSummary ? (
            <OfficeToAndFroDashboardMetricsSection stats={stats} loadingStats={loadingStats} />
          ) : null}

          {!perms.permissionsLoading && showCreate ? (
            <OfficeToAndFroFormSection
              masterData={masterDataForForm}
              onSuccess={handleMovementCreated}
            />
          ) : null}

          {!perms.permissionsLoading && showDirectory ? (
            <OfficeToAndFroDirectorySection
              movements={movements}
              masters={masters}
              fetchDirectoryFilterOptions={loadDirectoryFilterOptions}
              draftDirectoryFilters={draftDirectoryFilters}
              onDraftDirectoryFiltersChange={setDraftDirectoryFilters}
              onClearDirectoryFilters={() => setDraftDirectoryFilters(toFroDefaultDirectoryFilters())}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
              getToAndFroExportData={perms.canExport ? getToAndFroExportData : undefined}
              loadingMovements={loadingMovements}
              directoryPage={directoryPage}
              directoryTotalPages={directoryTotalPages}
              directoryTotal={directoryTotal}
              directoryPageSize={DIRECTORY_PAGE_SIZE}
              onDirectoryPageChange={setDirectoryPage}
              onView={handleViewMovement}
              onEdit={undefined}
              onReturn={canReturnActions ? handleReturnMovement : undefined}
              onDelete={undefined}
            />
          ) : null}
        </div>

        {quickJumpItems.length > 0 ? (
          <OfficeToAndFroQuickJumpSection
            asideAriaLabel="Office To and Fro sections"
            items={quickJumpItems}
            activeKey={activeQuickJump}
            onActiveChange={setActiveQuickJump}
          />
        ) : null}
      </div>

      {selectedViewMovementId ? (
        <ViewMovementModal
          movementId={selectedViewMovementId}
          onClose={() => setSelectedViewMovementId(null)}
        />
      ) : null}

      {selectedMarkReturnedMovement ? (
        <MarkReturnedModal
          isOpen={!!selectedMarkReturnedMovement}
          onClose={() => setSelectedMarkReturnedMovement(null)}
          movement={selectedMarkReturnedMovement}
          onSuccess={onMarkReturnedConfirm}
        />
      ) : null}
    </div>
  );
};

export default ToAndFroPage;
